export type ColorModeType = 'dark' | 'light';

export type ColorFunctionType = (opacity?: number | null) => string;

export type ColorsType = {
  readonly AVAILABLE1: ColorFunctionType,
  readonly AVAILABLE2: ColorFunctionType,
  readonly UNAVAILABLE1: ColorFunctionType,
  readonly UNAVAILABLE2: ColorFunctionType,
  readonly ACCENT1: ColorFunctionType,
  readonly ACCENT2: ColorFunctionType,
  readonly BACKGROUND1: ColorFunctionType,
  readonly PRIMARY1: ColorFunctionType,
  readonly PRIMARY2: ColorFunctionType,
  readonly PRIMARY3: ColorFunctionType,
  readonly PRIMARY4: ColorFunctionType,
  readonly PRIMARY5: ColorFunctionType,
  readonly PRIMARY6: ColorFunctionType,
  readonly PRIMARY7: ColorFunctionType,
  readonly SECONDARY1: ColorFunctionType,
  readonly SECONDARY2: ColorFunctionType,
  readonly HIGHLIGHT1: ColorFunctionType,
  readonly HIGHLIGHT2: ColorFunctionType,
  readonly HIGHLIGHT3: ColorFunctionType,
  readonly SELECT1: ColorFunctionType,
  readonly SHADOW1: ColorFunctionType,
  readonly SHADOW2: ColorFunctionType,
  readonly WARNING1: ColorFunctionType,
  readonly APP1_PRIMARY: ColorFunctionType,
  readonly APP1_SECONDARY: ColorFunctionType,
};

export type RgbaFunctionType = (
  red: number, green: number, blue: number, opacity: number | null
) => string;

export const rgba: RgbaFunctionType = (
  red,
  green,
  blue,
  opacity = 1,
) => `rgba(${red}, ${green}, ${blue}, ${opacity})`;

export const LIGHT_MODE_COLORS: ColorsType = Object.freeze({
  AVAILABLE1: (opacity = 1) => rgba(4, 240, 106, opacity),
  AVAILABLE2: (opacity = 1) => rgba(235, 255, 243, opacity),
  UNAVAILABLE1: (opacity = 1) => rgba(255, 46, 0, opacity),
  UNAVAILABLE2: (opacity = 1) => rgba(255, 238, 235, opacity),
  ACCENT1: (opacity = 1) => rgba(35, 46, 209, opacity),
  ACCENT2: (opacity = 1) => rgba(153, 160, 56, opacity),
  BACKGROUND1: (opacity = 1) => rgba(247, 246, 243, opacity),
  PRIMARY1: (opacity = 1) => rgba(0, 0, 0, opacity),
  PRIMARY2: (opacity = 1) => rgba(51, 51, 51, opacity),
  PRIMARY3: (opacity = 1) => rgba(79, 79, 79, opacity),
  PRIMARY4: (opacity = 1) => rgba(130, 130, 130, opacity),
  PRIMARY5: (opacity = 1) => rgba(189, 189, 189, opacity),
  PRIMARY6: (opacity = 1) => rgba(224, 224, 224, opacity),
  PRIMARY7: (opacity = 1) => rgba(235, 235, 235, opacity),
  SECONDARY1: (opacity = 1) => rgba(255, 255, 255, opacity),
  SECONDARY2: (opacity = 1) => rgba(235, 255, 243, opacity),
  HIGHLIGHT1: (opacity = 1) => rgba(231, 243, 246, opacity),
  HIGHLIGHT2: (opacity = 1) => rgba(53, 135, 164, opacity),
  HIGHLIGHT3: (opacity = 1) => rgba(146, 176, 188, opacity),
  SELECT1: (opacity = 1) => rgba(76, 117, 242, opacity),
  SHADOW1: (opacity = 1) => rgba(243, 246, 247, opacity),
  SHADOW2: (opacity = 1) => rgba(0, 0, 0, opacity),
  WARNING1: (opacity = 1) => rgba(215, 38, 56, opacity),
  APP1_PRIMARY: (opacity = 1) => rgba(243, 227, 10, opacity),
  APP1_SECONDARY: (opacity = 1) => rgba(187, 177, 133, opacity),
});

export const DARK_MODE_COLORS: ColorsType = Object.freeze({
  AVAILABLE1: (opacity = 1) => rgba(4, 240, 106, opacity),
  AVAILABLE2: (opacity = 1) => rgba(235, 255, 243, opacity),
  UNAVAILABLE1: (opacity = 1) => rgba(255, 46, 0, opacity),
  UNAVAILABLE2: (opacity = 1) => rgba(255, 238, 235, opacity),
  ACCENT1: (opacity = 1) => rgba(35, 46, 209, opacity),
  ACCENT2: (opacity = 1) => rgba(153, 160, 56, opacity),
  BACKGROUND1: (opacity = 1) => rgba(247, 246, 243, opacity),
  PRIMARY1: (opacity = 1) => rgba(0, 0, 0, opacity),
  PRIMARY2: (opacity = 1) => rgba(51, 51, 51, opacity),
  PRIMARY3: (opacity = 1) => rgba(79, 79, 79, opacity),
  PRIMARY4: (opacity = 1) => rgba(130, 130, 130, opacity),
  PRIMARY5: (opacity = 1) => rgba(189, 189, 189, opacity),
  PRIMARY6: (opacity = 1) => rgba(224, 224, 224, opacity),
  PRIMARY7: (opacity = 1) => rgba(235, 235, 235, opacity),
  SECONDARY1: (opacity = 1) => rgba(255, 255, 255, opacity),
  SECONDARY2: (opacity = 1) => rgba(235, 255, 243, opacity),
  HIGHLIGHT1: (opacity = 1) => rgba(231, 243, 246, opacity),
  HIGHLIGHT2: (opacity = 1) => rgba(53, 135, 164, opacity),
  HIGHLIGHT3: (opacity = 1) => rgba(146, 176, 188, opacity),
  SELECT1: (opacity = 1) => rgba(76, 117, 242, opacity),
  SHADOW1: (opacity = 1) => rgba(243, 246, 247, opacity),
  SHADOW2: (opacity = 1) => rgba(0, 0, 0, opacity),
  WARNING1: (opacity = 1) => rgba(215, 38, 56, opacity),
  APP1_PRIMARY: (opacity = 1) => rgba(243, 227, 10, opacity),
  APP1_SECONDARY: (opacity = 1) => rgba(187, 177, 133, opacity),
});

export type ColorManagerType = {
  mode: ColorModeType;
  palettes: {
    light: ColorsType,
    dark: ColorsType,
  };
  getColor: (key: string) => ColorFunctionType;
  setMode: (mode: ColorModeType) => void;
};

export const colorManager: ColorManagerType = {
  mode: 'light' as ColorModeType,
  palettes: {
    light: LIGHT_MODE_COLORS as ColorsType,
    dark: DARK_MODE_COLORS as ColorsType,
  },
  getColor(key: string): ColorFunctionType {
    return this.palettes[this.mode as ColorModeType][key as keyof ColorsType];
  },
  setMode(mode: ColorModeType): void {
    this.mode = mode;
  },
};

export const colors = new Proxy(
  {} as ColorsType,
  {
    get(target: ColorsType, property: string) {
      return colorManager.getColor(property);
    },
  },
);
